import React, { useEffect, useState, useCallback } from 'react';

import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { FaFileInvoiceDollar, FaCalendarTimes, FaStarHalfAlt, FaCheckCircle, FaChartBar } from "react-icons/fa";

import '../../../css/UserAdmin/AnalyticsSection.css';
import CardModule from '../CardModule';

const AccountingAnalytics = ({ hasAnalyticsAccess }) => {
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [yearsLoading, setYearsLoading] = useState(true);
  const [yearsError, setYearsError] = useState(null);

  const [loading, setLoading] = useState(false);
  const [paymentStats, setPaymentStats] = useState({
    pendingValidation: 0,
    overdue: 0,
    partiallyPaid: 0,
    fullyPaid: 0,
  });
  const [monthlyCollectionData, setMonthlyCollectionData] = useState([]);
  const [paymentCompletionData, setPaymentCompletionData] = useState([]);

  const COLORS = ['#00C49F', '#FF8042']; 

  useEffect(() => {
    const fetchAcademicYears = async () => {
      setYearsLoading(true);
      setYearsError(null);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/years?includeArchived=true&includeInactive=true`);
        if (!response.ok) throw new Error('Failed to fetch academic years');
        const data = await response.json();
        const years = data.data || [];
        setAcademicYears(years);

        const activeYear = years.find(year => year.status === 'Active');
        if (activeYear) setSelectedYear(activeYear.Year);
        else if (years.length > 0) setSelectedYear(years[0].Year);
      } catch (error) {
        console.error("Error fetching academic years:", error);
        setYearsError('Could not load academic years.');
      } finally {
        setYearsLoading(false);
      }
    };
    fetchAcademicYears();
  }, []);

  const generateSampleData = useCallback((year) => {
    if (!year) return;
    setLoading(true);
    const baseSeed = parseInt(year.substring(0, 4));
    const fullyPaid = 400 + (baseSeed % 60);
    const partiallyPaid = 80 + (baseSeed % 20);
    const overdue = 35 + (baseSeed % 15);
    const pendingValidation = 25 + (baseSeed % 10);
    setPaymentStats({ pendingValidation, overdue, partiallyPaid, fullyPaid });
    const months = ['Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May'];
    const collectionData = months.map(month => ({
      month,
      collected: Math.floor(Math.random() * 500000) + 200000
    }));
    setMonthlyCollectionData(collectionData);
    setPaymentCompletionData([
      { name: 'Fully Paid', count: fullyPaid, fill: COLORS[0] },
      { name: 'Unpaid/Partial', count: partiallyPaid + overdue, fill: COLORS[1] },
    ]);
    setTimeout(() => setLoading(false), 500);
  }, []);

  useEffect(() => {
    if (hasAnalyticsAccess && selectedYear) {
      generateSampleData(selectedYear);
    }
  }, [selectedYear, hasAnalyticsAccess, generateSampleData]);

  if (!hasAnalyticsAccess) return null;

  const formatCurrency = (value) => `₱${new Intl.NumberFormat('en-US').format(value)}`;

  return (
    <div className="analytics-section accounting-analytics">
      <div className="analytics-header">
        <h3 style={{ margin: 0, color: '#2c3e50', fontSize: '16px' }}>Accounting Analytics</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label htmlFor="academic-year-selector-accounting">Academic Year:</label>
          <select
            id="academic-year-selector-accounting"
            className="timeframe-selector"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            disabled={yearsLoading || loading}
          >
            {yearsLoading ? <option>Loading years...</option> : yearsError ? <option>{yearsError}</option> :
              academicYears.map(year => (
                <option key={year._id} value={year.Year}>
                  {year.Year} {year.status === 'Active' && '(Active)'}
                </option>
              ))
            }
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading-analytics-indicator">Loading payment data...</div>
      ) : (
        <>
          {/* --- MODIFICATION STARTS HERE --- */}
          <div className="card-container" style={{ marginBottom: '2rem' }}>
            <CardModule
              title="Process Payments"
              description="Validate and confirm new payments"
              statistics={paymentStats.pendingValidation.toString()}
              icon={FaFileInvoiceDollar}
              path={'/user-admin/payments'}
            />
            <CardModule
              title="Partially Paid"
              description="Incomplete Payment"
              statistics={paymentStats.partiallyPaid.toString()}
              icon={FaStarHalfAlt}
            />
            <CardModule
              title="Overdue"
              description="Past Due"
              statistics={paymentStats.overdue.toString()}
              icon={FaCalendarTimes}
            />
            <CardModule
              title="Fully Paid"
              description="Paid in Full"
              statistics={paymentStats.fullyPaid.toString()}
              icon={FaCheckCircle}
            />
          </div>
          {/* --- MODIFICATION ENDS HERE --- */}

          <div className="charts-row-container">
            <div className="chart-card">
              <h3 className="chart-title"><FaChartBar /> Total Matriculation Fee Collected Per Month</h3>
              <p className="chart-description">Payment Trend Analysis</p>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyCollectionData} margin={{ top: 20, right: 20, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={formatCurrency} width={80} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="collected" name="Collected" fill="#3498db" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-card">
              <h3 className="chart-title"><FaChartBar /> Payment Completion Comparison</h3>
              <p className="chart-description">Paid/Unpaid Comparison</p>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={paymentCompletionData} margin={{ top: 20, right: 20, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" name="Student Count">
                    {paymentCompletionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AccountingAnalytics;